import React from 'react'
import { FileSignature, CheckCircle, Eye, Upload, RefreshCw } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { cn } from '@/lib/utils'
import type { HubColor } from './HubShell'

export interface EsignDocumentItem {
  key: string
  title: string
  description: string
  signed: boolean
  actionType: 'e-sign' | 'upload' | 'view-only'
  uploadedFileName?: string
  uploaded?: boolean
  canExtract?: boolean
  extracting?: boolean
  extracted?: boolean
  onSign?: () => void
  onView?: () => void
  onUpload?: () => void
  onExtract?: () => void
  onReplace?: () => void
  disabled?: boolean
}

export interface EsignParty {
  label: string
  email?: string
  documents: EsignDocumentItem[]
}

interface InfoBanner {
  title: string
  subtitle: string
}

const panelColorClasses: Record<HubColor, {
  actionButton: string
  cardBorder: string
  cardBg: string
}> = {
  green: {
    actionButton: 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white border-0 shadow-lg',
    cardBorder: 'border-green-300 dark:border-green-700',
    cardBg: 'bg-green-50/40 dark:bg-green-900/10',
  },
  blue: {
    actionButton: 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white border-0 shadow-lg',
    cardBorder: 'border-blue-300 dark:border-blue-700',
    cardBg: 'bg-blue-50/40 dark:bg-blue-900/10',
  },
  purple: {
    actionButton: 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white border-0 shadow-lg',
    cardBorder: 'border-purple-300 dark:border-purple-700',
    cardBg: 'bg-purple-50/40 dark:bg-purple-900/10',
  },
  orange: {
    actionButton: 'bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white border-0 shadow-lg',
    cardBorder: 'border-orange-300 dark:border-orange-700',
    cardBg: 'bg-orange-50/40 dark:bg-orange-900/10',
  },
  cyan: {
    actionButton: 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-700 hover:to-cyan-600 text-white border-0 shadow-lg',
    cardBorder: 'border-cyan-300 dark:border-cyan-700',
    cardBg: 'bg-cyan-50/40 dark:bg-cyan-900/10',
  },
}

interface EsignDocumentsPanelProps {
  banner?: InfoBanner
  parties: EsignParty[]
  canSign?: boolean
  blockedReason?: string
  readOnly?: boolean
  color?: HubColor
  showSectionHeader?: boolean
}

export const EsignDocumentsPanel: React.FC<EsignDocumentsPanelProps> = ({
  banner,
  parties,
  canSign = true,
  blockedReason,
  readOnly = false,
  color = 'green',
  showSectionHeader = true,
}) => {
  const colorClasses = panelColorClasses[color]
  return (
    <div className="space-y-6">
      {banner && (
        <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
          <div className="flex items-start space-x-3">
            <FileSignature
              className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{banner.title}</p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{banner.subtitle}</p>
            </div>
          </div>
        </div>
      )}

      {showSectionHeader && (
        <div className="flex items-center space-x-2">
          <FileSignature className="h-5 w-5 text-primary-600" aria-hidden="true" />
          <h3 className="text-lg font-semibold">E-Sign Documents</h3>
        </div>
      )}

      {!canSign && blockedReason && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <FileSignature className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                {blockedReason}
              </p>
            </div>
          </div>
        </div>
      )}

      {parties.map((party, partyIndex) => (
        <div key={partyIndex} className="space-y-4">
          <div className="border-b border-border pb-2">
            <h4 className="text-base font-semibold text-foreground" aria-label={party.label}>
              {party.label}
            </h4>
            {party.email && (
              <p className="text-xs text-muted-foreground mt-1">{party.email}</p>
            )}
          </div>

          {party.documents.map((doc) => (
            <EsignDocumentCard
              key={doc.key}
              doc={doc}
              canSign={canSign}
              readOnly={readOnly}
              colorClasses={colorClasses}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

interface EsignDocumentCardProps {
  doc: EsignDocumentItem
  canSign: boolean
  readOnly: boolean
  colorClasses: typeof panelColorClasses[HubColor]
}

const EsignDocumentCard: React.FC<EsignDocumentCardProps> = ({
  doc,
  canSign,
  readOnly,
  colorClasses,
}) => {
  if (doc.actionType === 'upload') {
    return (
      <UploadDocumentCard
        doc={doc}
        readOnly={readOnly}
        colorClasses={colorClasses}
      />
    )
  }
  if (doc.actionType === 'view-only') {
    return <ViewOnlyDocumentCard doc={doc} colorClasses={colorClasses} />
  }

  const signed = doc.signed
  const signDisabled = !canSign || doc.disabled || readOnly

  return (
    <Card className={cn('p-4', colorClasses.cardBorder, colorClasses.cardBg)}>
      <div className="space-y-3">
        <div>
          <h5 className="text-sm font-medium text-foreground/80">{doc.title}</h5>
          <p className="text-xs text-muted-foreground mt-1">
            {signed ? 'E-signed and completed' : doc.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {signed ? (
            <>
              <CheckCircle
                className="h-5 w-5 text-green-600 dark:text-green-400"
                aria-hidden="true"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={doc.onView}
                aria-label={`View ${doc.title}`}
              >
                <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
                View
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={doc.onSign}
              disabled={signDisabled}
              aria-label={`E-Sign ${doc.title}`}
              className={cn(
                colorClasses.actionButton,
                signDisabled && 'opacity-50 cursor-not-allowed pointer-events-none'
              )}
            >
              <FileSignature className="h-4 w-4 mr-1" aria-hidden="true" />
              E-Sign {doc.title}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

interface ViewOnlyDocumentCardProps {
  doc: EsignDocumentItem
  colorClasses: typeof panelColorClasses[HubColor]
}

const ViewOnlyDocumentCard: React.FC<ViewOnlyDocumentCardProps> = ({ doc, colorClasses }) => {
  return (
    <Card className={cn('p-4', colorClasses.cardBorder, colorClasses.cardBg)}>
      <div className="space-y-3">
        <div>
          <h5 className="text-sm font-medium text-foreground/80">{doc.title}</h5>
          <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle
            className="h-5 w-5 text-green-600 dark:text-green-400"
            aria-hidden="true"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={doc.onView}
            disabled={doc.disabled}
            aria-label={`View / Download ${doc.title}`}
          >
            <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
            View / Download
          </Button>
        </div>
      </div>
    </Card>
  )
}

interface UploadDocumentCardProps {
  doc: EsignDocumentItem
  readOnly: boolean
  colorClasses: typeof panelColorClasses[HubColor]
}

const UploadDocumentCard: React.FC<UploadDocumentCardProps> = ({ doc, readOnly, colorClasses }) => {
  const done = doc.uploaded || doc.extracted
  const showExtract = doc.uploaded && !doc.extracted && doc.canExtract && !readOnly

  return (
    <Card
      className={cn(
        'p-4',
        done
          ? 'border-green-200 dark:border-green-800'
          : colorClasses.cardBorder,
        done ? '' : colorClasses.cardBg
      )}
    >
      <div className="space-y-3">
        <div>
          <h5 className="text-sm font-medium text-foreground/80">{doc.title}</h5>
          <p className="text-xs text-muted-foreground mt-1">
            {doc.extracted
              ? 'Processed by Neela AI'
              : doc.uploaded
              ? `Uploaded: ${doc.uploadedFileName || 'file'}`
              : doc.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {done && (
            <CheckCircle
              className="h-5 w-5 text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
          )}
          {showExtract && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={doc.onExtract}
              disabled={doc.extracting}
              aria-label={`Process ${doc.title} with Neela AI`}
            >
              {doc.extracting ? 'Processing…' : 'Process with Neela AI'}
            </Button>
          )}
          {done && !readOnly && doc.onReplace && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={doc.onReplace}
              aria-label={`Replace ${doc.title}`}
            >
              <RefreshCw className="h-4 w-4 mr-1" aria-hidden="true" />
              Replace
            </Button>
          )}
          {!done && !readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={doc.onUpload}
              disabled={doc.disabled}
              aria-label={`Upload ${doc.title}`}
            >
              <Upload className="h-4 w-4 mr-1" aria-hidden="true" />
              Upload
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
